import React from 'react'
import { Button,  Tooltip, Empty  } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { injectIntl } from 'react-intl'
import axios from 'axios'
import _ from 'lodash'
import endpoints from 'services/endpoints'
import { showErrorMessage } from 'services/handleErrors'
import errorBoundaryEnhancer from 'services/errorBoundaryEnhancer'
import { eventTypesMap, parseLocalTimeByPattern } from 'services/utils'
import { redirectTo } from 'services/router'
import { parseEventState } from 'pages/finance/utils'
import SpinLoader from 'components/SpinLoader'
import FinanceRow from 'components/FinanceRow'
import FinanceEventIcon from 'components/FinanceEventIcon'
import { WITHDRAWAL_TYPES } from 'pages/finance/new-withdrawal/constants'

import style from './style.module.scss'

const EventsTable = injectIntl(({ events, intl: { formatMessage } }) => {
  if (!events || events.length < 1) {
    return <Empty className={style.placeholder} image={Empty.PRESENTED_IMAGE_SIMPLE} />
  }

  return (
    <table className={style.table}>
      <tbody>
        {events.map((event) => {
          const eventData = event.withdrawal || event.payment || event.remittance || event.payout;

          // title
          const title = event.type ? formatMessage({ id: eventTypesMap[event.type] }) : '';

          // description
          let description = '';

          if (event.type === 'EventByWithdrawal') {
            const withdrawalType = _.get(event, 'withdrawal.type', null)

            if (withdrawalType === WITHDRAWAL_TYPES.TO_BANK_CARD) {
              description = formatMessage({ id: 'page.finance.new-withdrawal.to-bank-card' })
            }

            if (withdrawalType === WITHDRAWAL_TYPES.TO_CHECKING_ACCOUNT) {
              description = formatMessage({ id: 'page.finance.new-withdrawal.to-checking-account' })
            }

            description = description.toLowerCase()
          }

          if (event.type === 'EventByPayment') {
            description = `${formatMessage({ id: 'page.dashboard.finance-events.eventByPayment-pretext' })} ${_.get(event, 'payment.product.title', null)}`;
          }

          if (event.type === 'EventByPayout') {
            description = _.get(event, 'payout.note', null);
          }

          if (['EventByReceipt', 'EventByRemittance'].indexOf(event.type) !== -1) {
            description = (
              <>
                {event.type === 'EventByReceipt' && formatMessage({ id: 'page.dashboard.finance-events.eventByReceipt-pretext' })}
                {event.type === 'EventByRemittance' && formatMessage({ id: 'page.dashboard.finance-events.eventByRemittance-pretext' })}
                &nbsp;
                <Tooltip title={`@${_.get(event, 'remittance.counterparty.login', '')}`}>
                  {_.get(event, 'remittance.counterparty.firstname', '')}&nbsp;
                  {_.get(event, 'remittance.counterparty.lastname', '')}
                </Tooltip>
              </>
            )
          }

          const parsedStatus = parseEventState(eventData.state);
          const status = <FinanceEventIcon type={event.type} status={parsedStatus} />

          // date
          let date = eventData.createdAt || eventData.updatedAt;
          const [dateString, dateTimeString] = parseLocalTimeByPattern(date, 'DD MMM YYYY&LL – HH:mm').split('&')

          date = <Tooltip title={dateTimeString}>{dateString}</Tooltip>

          return (
            <FinanceRow
              key={event.id}
              status={status}
              title={title}
              date={date}
              amount={event.amount}
              description={description}
              rejected={parsedStatus === 'page.finance.event.status.rejected'}
              swapSubRows
            />
          )
        })}
      </tbody>
    </table>
  )
})

class FinanceEvents extends React.Component {
  state = {
    events: [],
    loading: true
  }

  componentDidMount() {
    this.getAllEvents()
  }

  getAllEvents = () => {
    this.setState({ loading: true })
    axios
      .post(endpoints.getAllEvents, { limit: 3, page: 1 })
      .then(res => {
        const events = _.get(res, 'data.events', [])

        this.setState({ events, loading: false })
      })
      .catch(err => {
        showErrorMessage(err)
        this.setState({ loading: false })
      })
  }

  render() {
    const { formatMessage } = this.props.intl;
    const { loading, events } = this.state;

    return (
      <div className={style.financeEvents}>
        <h3>{formatMessage({ id: 'page.dashboard.finance-events.title' })}</h3>

        {loading && <SpinLoader className={style.loading} />}
        <EventsTable events={events} />

        {events.length > 0 && (
          <Button
            onClick={() => redirectTo('/finance')}
            className={style.showMore}
            icon={<PlusOutlined />}
          >
            {formatMessage({ id: 'page.dashboard.finance-events.show-all' })}
          </Button>
        )}
      </div>
    )
  }
}

export default errorBoundaryEnhancer(injectIntl(FinanceEvents))
